import { useState, useEffect, useRef } from 'react';
import {
  getSavedMessages,
  saveMessages,
  getSavedSessionId,
  saveSessionId,
  clearMessages,
  generateSessionId
} from '../utils/storage';
import { sendChatMessage, resetConversation } from '../services/apiService';
import { API_CONFIG } from '../config';

export const useChat = () => {
  const [messages, setMessages] = useState(getSavedMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(getSavedSessionId);
  const [errorMessage, setErrorMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const genRef = useRef(0);

  useEffect(() => {
    saveMessages(messages);
    saveSessionId(sessionId);
  }, [messages, sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (text) => {
    const messageText = text || input.trim();
    if (!messageText) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setLoading(true);

    // Show notification on first message
    if (messages.length === 0) {
      setShowNotification(true);
    }

    const controller = new AbortController();
    abortRef.current = controller;
    const gen = genRef.current;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, API_CONFIG.TIMEOUT);

    try {
      const data = await sendChatMessage(messageText, sessionId, controller.signal);

      if (gen !== genRef.current) return;

      if (typeof data?.response !== 'string') throw new Error('Malformed response');

      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      if (gen !== genRef.current) return;

      if (error.name === 'AbortError') {
        if (timedOut) {
          setShowNotification(false);
          setErrorMessage('The server is taking too long to respond. This usually happens when the backend is waking up from a cold start. Please try again in a moment.');
        }
        // else: reset-triggered abort, stay silent
      } else {
        setShowNotification(false);
        setErrorMessage('Something went wrong reaching Spark. Please check your connection and try again.');
      }
    } finally {
      clearTimeout(timeoutId);
      if (gen === genRef.current) {
        setLoading(false);
      }
    }
  };

  const startNewConversation = async () => {
    if (messages.length === 0) return;

    genRef.current += 1;
    abortRef.current?.abort();
    setLoading(true);

    try {
      const success = await resetConversation(sessionId);
      if (success) {
        console.log('Conversation stored in Supabase');
      }
    } catch (error) {
      console.error('Error storing conversation:', error);
    }

    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    setMessages([]);
    setInput('');
    setShowNotification(false);
    setLoading(false);
    clearMessages();
    saveSessionId(newSessionId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return {
    messages,
    input,
    loading,
    sessionId,
    errorMessage,
    showNotification,
    messagesEndRef,
    setInput,
    setErrorMessage,
    setShowNotification,
    sendMessage,
    startNewConversation,
    handleKeyPress
  };
};
